/**
 * AI Onboarding Agent SDK - Shared Types & Constants
 * Contains all interfaces, types, constants, and utility functions used across SDK modules.
 */

import { logger } from './logger';
import type { DemoCapturePolicy } from '../components/demoLayout';
import type { ExitIntentConfig } from './exit-intent';

export type { DemoCapturePolicy };

/**
 * SDK Widget Selectors - Elements to exclude from DOM captures and targeting
 * These are our own UI elements that should not be visible to the LLM or clickable by the bot
 */
export const SDK_WIDGET_SELECTORS = [
  '#onboarding-overlay',       // Main overlay container (highlights, tooltips)
  '#floe-cursor',              // Animated bot cursor
  '#onboarding-sdk-react-ui',  // React UI components (voice indicator, etc.)
  '#hse-highlight-element',    // Element highlight overlay
  '#floe-cursor-styles',       // Style element for cursor
  '#onboarding-animations',    // Animation styles
] as const;

/** Data attribute to mark SDK widget elements */
export const SDK_WIDGET_ATTR = 'data-floe-widget';

/**
 * Daily user_name the avatar publisher joins the room under (FLO-545).
 * pipecat-server points the avatar session's video egress at the bot's own
 * Daily room using this exact user_name, and trackStarted identifies the
 * avatar's tracks by matching it (case-insensitive) — a positive identity
 * check, not a "any named non-bot participant" heuristic.
 * MUST match FLOE_AVATAR_USER_NAME in pipecat-server services/avatar_session.py.
 */
export const FLOE_AVATAR_USER_NAME = 'floe-avatar';

/**
 * Check if an element is an SDK widget that should be excluded
 */
export function isSDKWidget(el: HTMLElement | null): boolean {
  if (!el) return false;

  // Exclude the widget and its descendants from host-page observations.
  if (el.closest(`[${SDK_WIDGET_ATTR}]`)) return true;

  // Check if element or any parent matches SDK selectors
  for (const selector of SDK_WIDGET_SELECTORS) {
    if (el.closest(selector)) return true;
  }

  return false;
}

/**
 * Does a /sites/validate-client-key body have the shape we are willing to act on?
 *
 * Lives here rather than inline in onboarding-sdk.ts so a test can assert it without
 * booting the SDK — the same reason getSanitizedUrl is here.
 *
 * Two failure modes it exists to stop, both of which otherwise end in the SDK
 * starting when it should not:
 *
 *  - `JSON.parse('null')` is legal. Reading `.valid` off null throws a TypeError,
 *    and the caller's catch treats TypeError as "network blip, proceed anyway" —
 *    so a malformed body would take the branch reserved for real outages.
 *  - `valid` must be a genuine boolean, not merely truthy. floe-api always sends
 *    one (sites.service.ts types it `valid: boolean` and only emits literals), but
 *    an intermediary that stringifies JSON turns `false` into "false", which is
 *    truthy and would otherwise read as permission to start.
 */
// Deliberately returns plain `boolean`, not a `result is {...}` type predicate. The
// caller reads a dozen optional brand/config fields off the same object afterwards
// (siteName, websiteAgent, demoAvailable, …); a predicate would narrow `result` to
// just the two fields named here and break every one of those reads.
export function isValidationResponse(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    !Array.isArray(result) &&
    typeof (result as { valid?: unknown }).valid === 'boolean'
  );
}

/**
 * Get sanitized current-page URL without query params or fragments.
 * Use this for any URL that will be transmitted externally to prevent leaking tokens/PII.
 */
export function getSanitizedUrl(): string {
  return window.location.origin + window.location.pathname;
}

/**
 * Sanitize an arbitrary URL string by stripping query params and fragments.
 * Returns origin + pathname only.  Falls back to the input unchanged if it
 * cannot be parsed (e.g. a bare relative path that the URL constructor rejects).
 */
export function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin + parsed.pathname;
  } catch {
    return url;
  }
}

/**
 * Strip Angular form state classes from CSS selectors.
 * Angular dynamically toggles .ng-untouched/.ng-touched, .ng-pristine/.ng-dirty, .ng-valid/.ng-invalid
 * on form controls, making selectors fragile. This removes those classes so selectors
 * remain valid regardless of form interaction state.
 */
export function stripAngularStateClasses(selector: string): string {
  if (!selector) return selector;
  return selector.replace(/\.ng-(untouched|touched|pristine|dirty|valid|invalid)/g, '');
}

/**
 * Try to find an element by selector, with automatic Angular state class fallback.
 * First tries the exact selector, then strips Angular state classes and retries.
 */
export function querySelectorWithAngularFallback(selector: string): HTMLElement | null {
  // Try exact selector first
  try {
    const el = document.querySelector(selector) as HTMLElement;
    if (el) return el;
  } catch (e) {
    // Invalid selector, will try cleaned version
  }
  
  // Try with Angular state classes stripped
  const cleaned = stripAngularStateClasses(selector);
  if (cleaned !== selector) {
    try {
      const el = document.querySelector(cleaned) as HTMLElement;
      if (el) {
        logger.log(`[AngularFallback] Selector matched after stripping state classes: "${cleaned}"`);
        return el;
      }
    } catch (e) {
      // Still invalid
    }
  }
  
  return null;
}

/**
 * Check if an element (or its ancestors) is a modal/dialog overlay.
 * These overlays block user interaction with elements underneath.
 */
export function isModalOverlay(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  let depth = 0;
  while (current && depth < 5) {
    // Check ARIA roles
    const role = current.getAttribute('role');
    if (role === 'dialog' || role === 'alertdialog') return true;
    if (current.getAttribute('aria-modal') === 'true') return true;
    
    // Check HTML dialog element
    if (current.tagName === 'DIALOG') return true;
    
    // Check common modal tag names (Angular, etc.)
    const tagLower = current.tagName.toLowerCase();
    if (tagLower.includes('modal') || tagLower.includes('dialog')) return true;
    
    // Check class names for modal/dialog indicators
    const classStr = typeof current.className === 'string' ? current.className.toLowerCase() : '';
    if (classStr.includes('modal') || classStr.includes('dialog') || classStr.includes('cdk-overlay')) return true;
    
    current = current.parentElement;
    depth++;
  }
  return false;
}

/**
 * Human-like click: dispatches events to whatever element is actually at the target's coordinates
 * 
 * This mimics how a real human click works:
 * 1. Calculate center coordinates of the target element
 * 2. Use document.elementFromPoint() to find what's actually at those coordinates
 * 3. Dispatch click events to that element (which might be a child or overlay)
 * 
 * This is more reliable than dispatching directly to a target element because:
 * - React-Select dropdown controls have nested clickable areas
 * - Overlays with pointer-events:none are handled correctly
 * - The browser resolves the actual clickable element just like a real click would
 * 
 * IMPORTANT: If a modal/dialog overlay is blocking the target, the click will FAIL
 * instead of bypassing the overlay. This prevents clicking buttons underneath modals.
 * 
 * @param targetElement - The element we want to click (used for coordinate calculation)
 * @param options - Additional options for the click behavior
 * @returns The element that actually received the click
 * @throws Error if a modal overlay is blocking the target element
 */
export function humanLikeClick(
  targetElement: HTMLElement,
  options: {
    focus?: boolean;
    includePointerEvents?: boolean;
    nativeClickFallback?: boolean;
    includeMouseEnter?: boolean;
  } = {}
): HTMLElement {
  const { 
    focus = true, 
    includePointerEvents = true, 
    nativeClickFallback = false,
    includeMouseEnter = false 
  } = options;

  // Calculate center coordinates of the target element
  const rect = targetElement.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  // Find what element is actually at those coordinates (human-like behavior)
  const elementAtPoint = document.elementFromPoint(centerX, centerY) as HTMLElement | null;

  // Determine the actual click target
  let clickTarget: HTMLElement;

  if (elementAtPoint && (
    elementAtPoint === targetElement ||
    targetElement.contains(elementAtPoint) ||
    elementAtPoint.contains(targetElement)
  )) {
    // Good - elementAtPoint is related to our target, use it for human-like click
    clickTarget = elementAtPoint;
    if (clickTarget !== targetElement) {
      logger.log('[humanLikeClick] 🎯 Using element at point:', clickTarget.tagName,
        'instead of target:', targetElement.tagName);
    }
  } else if (elementAtPoint) {
    // Mismatch - something else is at those coordinates (could be overlay, etc.)
    logger.warn('[humanLikeClick] ⚠️ Element at point is unrelated:',
      elementAtPoint.tagName, elementAtPoint.className?.toString().slice(0, 50));
    logger.warn('[humanLikeClick]    Expected target:', targetElement.tagName,
      targetElement.className?.toString().slice(0, 50));
    
    // CRITICAL: Check if the blocking element is a modal/dialog overlay
    // If so, DO NOT click through - this prevents clicking buttons underneath modals
    if (isModalOverlay(elementAtPoint)) {
      // EXCEPTION: If the TARGET is also inside a modal, this is a stacked/duplicate
      // modal case (e.g., two calendar modals). The target legitimately belongs to a
      // modal — use direct dispatch instead of blocking.
      if (isModalOverlay(targetElement)) {
        logger.warn('[humanLikeClick] ⚠️ Stacked modal detected: both target and overlay are in modals.',
          'Falling through to direct dispatch on target.');
        clickTarget = targetElement;
      } else {
        const overlayInfo = `${elementAtPoint.tagName} (${elementAtPoint.className?.toString().slice(0, 80)})`;
        logger.error(`[humanLikeClick] ❌ BLOCKED: Modal/dialog overlay is covering the target element.`,
          `Overlay: ${overlayInfo}`,
          `Target: ${targetElement.tagName} "${targetElement.textContent?.trim().slice(0, 50)}"`);
        throw new Error(
          `Click blocked by modal overlay: ${overlayInfo}. ` +
          `Target "${targetElement.textContent?.trim().slice(0, 50)}" is behind the modal. ` +
          `Interact with the modal instead.`
        );
      }
    }
    
    // Non-modal overlay (tooltip, etc.) - fall back to direct dispatch on target
    clickTarget = targetElement;
  } else {
    // No element at point (could be off-screen or hidden)
    logger.warn('[humanLikeClick] ⚠️ No element at coordinates, falling back to target');
    clickTarget = targetElement;
  }

  // Focus first (important for React components and form elements)
  if (focus && typeof clickTarget.focus === 'function') {
    clickTarget.focus();
  }

  // An explicitly associated, enabled checkbox/radio label has browser-defined
  // activation semantics. Its native activation emits exactly one associated-control
  // click; synthesizing a pointer/mouse sequence on the label made this host briefly
  // select the control and then restore the previous filter on its re-render.
  // This is intentionally narrow: unassociated labels and every other target keep
  // the established human-like event sequence below.
  const associatedToggle = targetElement instanceof HTMLLabelElement ? targetElement.control : null;
  if (associatedToggle instanceof HTMLInputElement &&
      (associatedToggle.type === 'checkbox' || associatedToggle.type === 'radio') &&
      !associatedToggle.disabled) {
    targetElement.click();
    logger.log('[humanLikeClick] ✅ Used native associated-toggle activation for', targetElement.tagName);
    return targetElement;
  }

  // Build event options with coordinates
  const eventOptions: MouseEventInit = {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX: centerX,
    clientY: centerY,
    screenX: centerX + window.screenX,
    screenY: centerY + window.screenY,
    button: 0,
    buttons: 1,
  };

  // Build pointer event options if needed
  const pointerOptions: PointerEventInit | null = includePointerEvents
    ? { ...eventOptions, pointerType: 'mouse', isPrimary: true }
    : null;

  // Include mouseenter/mouseover if requested (some components need this)
  if (includeMouseEnter) {
    clickTarget.dispatchEvent(new MouseEvent('mouseenter', { ...eventOptions, bubbles: false }));
    clickTarget.dispatchEvent(new MouseEvent('mouseover', eventOptions));
  }

  // Dispatch events in correct browser order: pointer and mouse events
  // INTERLEAVED as one interaction cycle. The old order (all pointer events
  // before all mouse events) caused Angular CDK to see two separate
  // interaction cycles, triggering duplicate modal instantiation.
  //
  // Real browser order: pointerdown → mousedown → pointerup → mouseup → click
  if (pointerOptions) {
    clickTarget.dispatchEvent(new PointerEvent('pointerdown', pointerOptions));
  }
  clickTarget.dispatchEvent(new MouseEvent('mousedown', eventOptions));
  if (pointerOptions) {
    clickTarget.dispatchEvent(new PointerEvent('pointerup', pointerOptions));
  }
  clickTarget.dispatchEvent(new MouseEvent('mouseup', eventOptions));
  clickTarget.dispatchEvent(new MouseEvent('click', eventOptions));

  // Native click as backup (helps with some edge cases)
  if (nativeClickFallback && typeof clickTarget.click === 'function') {
    clickTarget.click();
  }

  logger.log('[humanLikeClick] ✅ Completed on', clickTarget.tagName,
    'at (', Math.round(centerX), ',', Math.round(centerY), ')');

  return clickTarget;
}

/**
 * AudioAnalyzer - Real-time audio level analysis using Web Audio API
 * Provides FFT-based frequency analysis for waveform visualization
 */
export class AudioAnalyzer {
  private audioContext: AudioContext | null = null;
  private analyzerNode: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private dataArray: Uint8Array = new Uint8Array(0);
  private isRunning: boolean = false;
  private onLevelsCallback: ((levels: number[]) => void) | null = null;
  private animationId: number | null = null;

  constructor() {
    // AudioContext will be created when needed (requires user interaction)
  }

  /**
   * Start analyzing audio from a MediaStream (mic or bot audio)
   */
  start(stream: MediaStream, onLevels: (levels: number[]) => void): void {
    try {
      // Create audio context if needed
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      // Resume if suspended (browsers require user interaction)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      // Create analyzer node
      this.analyzerNode = this.audioContext.createAnalyser();
      this.analyzerNode.fftSize = 64; // 32 frequency bins (small for simple visualization)
      this.analyzerNode.smoothingTimeConstant = 0.4; // Smoothing for natural movement

      // Connect stream to analyzer
      this.sourceNode = this.audioContext.createMediaStreamSource(stream);
      this.sourceNode.connect(this.analyzerNode);

      // Create data array for frequency data
      const bufferLength = this.analyzerNode.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      // Store callback
      this.onLevelsCallback = onLevels;
      this.isRunning = true;

      // Start analysis loop
      this.analyze();

      logger.log('[AudioAnalyzer] Started analyzing audio stream');
    } catch (error) {
      logger.error('[AudioAnalyzer] Failed to start:', error);
    }
  }

  /**
   * Analysis loop - runs via requestAnimationFrame
   */
  private analyze = (): void => {
    if (!this.isRunning || !this.analyzerNode || this.dataArray.length === 0) return;

    // Get frequency data (type assertion needed for TS compatibility)
    (this.analyzerNode as any).getByteFrequencyData(this.dataArray);

    // Convert to normalized levels (0-1)
    const levels: number[] = [];
    for (let i = 0; i < this.dataArray.length; i++) {
      // Focus on voice frequencies (skip very low and very high)
      if (i >= 2 && i < this.dataArray.length - 2) {
        levels.push(this.dataArray[i] / 255);
      }
    }

    // Call callback with levels
    if (this.onLevelsCallback) {
      this.onLevelsCallback(levels);
    }

    // Continue loop
    this.animationId = requestAnimationFrame(this.analyze);
  };

  /**
   * Stop analyzing
   */
  stop(): void {
    this.isRunning = false;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    this.analyzerNode = null;
    this.dataArray = new Uint8Array(0);
    this.onLevelsCallback = null;

    logger.log('[AudioAnalyzer] Stopped');
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Types
export interface UserInfo {
  phone?: string;
  externalId?: string;    // Customer's user ID
  email?: string;
  name?: string;
  company?: string;
  designation?: string;
  metadata?: Record<string, any>;
}

/**
 * Supplies a fresh bearer token for an authenticated support MCP session.
 *
 * The callback is invoked once when an `on_demand` support session is about
 * to start. Its result is reused for that MCP session, so the token's remaining
 * lifetime should cover the expected conversation.
 */
export type FloeMcpBearerTokenProvider = () =>
  string | null | undefined | Promise<string | null | undefined>;

/**
 * Authenticated remote MCP tools available to the on-demand support agent.
 *
 * The browser never discovers or exposes every tool automatically. Callers
 * must name the read-only tools this widget is allowed to offer, while the MCP
 * server remains responsible for authorizing the supplied user's token.
 */
export interface FloeMcpConfig {
  /** HTTPS MCP Streamable HTTP endpoint, e.g. https://mcp.example.com/mcp. */
  serverUrl: string;
  /** Resolve the current user's bearer token without a `Bearer ` prefix. */
  getBearerToken: FloeMcpBearerTokenProvider;
  /**
   * Exact MCP tool names this support widget may expose (maximum 20).
   * Each name is 1–128 ASCII letters, digits, underscores, dots, or dashes.
   */
  allowedTools: readonly string[];
}

export interface SDKConfig {
  clientKey: string;  // Public client key - safe to expose in browser, used for domain-bound authentication
  apiUrl?: string;  // Floe API URL (defaults to baked-in value from build: __FLOE_API_URL__)
  demoMode?: boolean;  // Demo agent mode — shows DemoView with video + chat sidebar
  // FLO-320: website-agent mode — a top-of-funnel Q&A chat that runs on the SAME
  // merged pipecat session as the demo agent. OFF by default. When on, the SDK
  // renders a corner launcher — FLO-520: an avatar video tile when the avatar is
  // enabled and has configured media, else the "Ask {agent}" pill — plus a bottom
  // row of page-scoped nudge chips. Opening the launcher immediately warm-starts
  // the Q&A session, mic-muted and with NO email gate, sending
  // `entryPhase: "q_and_a"` in the /sites/start-session body. Every deliberate
  // entry gets an immediate agent turn: a welcome for the primary/keyboard
  // launchers, or a direct answer when a typed/nudge prompt starts the session.
  // Keyboard entry renders that turn with speaker audio muted. Typed prompts
  // and demo actions reuse the session without microphone access; only an
  // explicit voice opt-in requests it. Mutually distinct from demoMode — when
  // both are set, demoMode wins (see AgentUI branch order).
  websiteAgent?: boolean;
  // FLO-514/519: website-agent display mode + rail width. Optional init-time
  // OVERRIDES for the per-site values resolved by /sites/validate-client-key
  // (`websiteAgent.displayMode` / `websiteAgent.sidebarWidth`) — undefined means
  // "use the site config". 'sidebar' docks a full-height right rail and squeezes
  // the host page at ≥1024px; anything else renders today's centered card.
  // Kept in sync with the FloeConfig copy in sdk/index.ts.
  websiteAgentDisplayMode?: 'floating' | 'sidebar';
  websiteAgentSidebarWidth?: number;  // clamped 320–480 at the render layer; default 400
  /**
   * Silence, in milliseconds, after which the live avatar track is replaced by
   * the site's idle loop. Default 8000; `0` disables; values below 3000 are
   * clamped (see `sdk/avatar-idle.ts` for why the floor exists).
   *
   * Presentation only — it does not reduce what the video avatar costs.
   */
  avatarIdleAfterMs?: number;
  demoSiteId?: string;  // Site ID for demo mode session spawning
  demoLinkId?: string;  // FLO-150: DemoLink id when launched from a /d/[token] link. Forwarded to the bot so the link's persona override applies.
  demoCalendarLink?: string;  // Calendar link for "Book a live demo" fallback button
  // Demo overlay branding (FLO-64). Optional init-time overrides; when unset
  // the SDK uses the per-site values from /sites/validate-client-key, and when
  // those are unset too the overlay falls back to Floe defaults. Lets a host
  // (e.g. a shareable-link page) apply a per-link brand override.
  demoAccentColor?: string;  // Brand accent as #RRGGBB hex (overlay accent palette)
  demoCtaLabel?: string;  // Primary booking CTA label (default "Book a call")
  // FLO-285: how the demo overlay renders when embedded on a host page.
  //   'docked'     — start minimized as a bottom-docked email-capture pill,
  //                  then go full-bleed once the demo starts. For marketing
  //                  embeds where a full-screen takeover on load is too heavy.
  //   'fullscreen' — open the intro form full-screen immediately, then run
  //                  full-bleed (today's behaviour). For dedicated demo pages
  //                  like /demo/[prospect] and /d/[token].
  // Defaults to 'docked' at the Floe() boundary (see index.ts).
  embedMode?: 'docked' | 'fullscreen';
  enableVideo?: boolean;
  enableAudio?: boolean;
  enableScreenCapture?: boolean;
  redactionPatterns?: Record<string, RegExp>;
  debug?: boolean;
  // Optional context for personalized onboarding
  industry?: string;
  useCase?: string;
  companyName?: string;
  companySize?: string;
  role?: string;
  // User identification
  userInfo?: UserInfo;
  // Skip onboarding modal - auto-detects if not set, use true/false to override
  skipOnboardingModal?: boolean;
  // Discovery mode - guided onboarding for new users
  enableDiscoveryPopup?: boolean;  // Auto-show welcome popup for first-time users (default: true)
  // Nudge configuration - Intercom Fin-style sliding notification
  nudge?: {
    text?: string;  // Main nudge message (default: "Hi 👋 How can I help you today?")
    // Auto-show nudge when minimized. undefined = "customer didn't say" — the
    // default is mode-dependent and resolved by resolveNudgeAutoShow()
    // (true normally, false under activation:'on_demand'). Deliberately NOT
    // defaulted at the Floe() boundary, unlike its two siblings (FLO-534).
    autoShow?: boolean;
    autoHideDelay?: number;  // Auto-hide after ms, 0 = never (default: 0)
  };
  // Opt-in, one-shot desktop exit intent for marketing agents. The config is
  // normalized at the Floe() boundary; the surface never starts a session.
  exitIntent?: boolean | ExitIntentConfig;
  // FLO-585: where the website, onboarding, or support launcher docks, and how
  // far from the edge.
  // Bottom-right is the most contested 80px on the web (Intercom, Pendo, Appcues
  // all default there), so a customer running one of them AND Floe needs a way
  // out that isn't a CSS override against our z-index. Default bottom-right.
  launcher?: {
    position?: 'bottom-right' | 'bottom-left';
    inset?: number;  // px from the viewport edge (default 24)
  };
  // Pre-supplied prospect email. When set, the SDK's DemoIntroForm skips
  // its email stage and starts at name + optional fields.
  prospectEmail?: string;
  /**
   * WHEN the demo asks who the prospect is. Overrides the site-level policy
   * that `validateClientKey` serves — set by the /d/{token} launch shell, which
   * gets the already-merged (site ← link) answer from the resolved demo link.
   *
   * Undefined means "use whatever the site serves", which for an older floe-api
   * is nothing at all — and no policy resolves to today's gating intro form.
   * See `captureIsDeferred` in components/demoLayout for why a deferred policy
   * still costs one tap.
   */
  demoIdentityCapture?: DemoCapturePolicy;
  // FLO-534: onboarding-agent activation posture. 'on_demand' ("support mode")
  // keeps the agent dormant — no welcome popup, no session spent on page load —
  // until the user clicks the launcher, and tells the server to open reactively
  // instead of pitching a walkthrough. Resolved to a real value at the Floe()
  // boundary; undefined here means an SDK constructed outside it, which reads
  // as 'proactive' (today's behaviour) everywhere it's consumed.
  activation?: 'proactive' | 'on_demand';
  // FLO-661: authenticated MCP data tools for support mode only. The token is
  // deliberately a provider callback, not a captured value; it is resolved
  // immediately before the user's support click starts a session.
  mcp?: FloeMcpConfig;
  // Per-visitor consent gating for SDK-collected identity. 'pending' defers
  // every identity write/attach (anonymous id, visitor-token mint, visitor +
  // nudge events, conversation open) until `consent('granted')`; undefined
  // means "use the per-site `websiteAgent.consentModeDefault` from
  // validate-client-key", which resolves to 'not-required' (today's behavior)
  // when the server says nothing. Kept in sync with the FloeConfig copy.
  consentMode?: 'not-required' | 'pending';
}

// EndUser status from API (for skip onboarding logic)
export interface EndUserStatusRequest {
  clientKey: string;
  origin: string;
  /**
   * Existing server-issued browser continuity anchor. This bootstrap is an
   * exact-source read: email/externalId are intentionally not part of the
   * public contract, and callers must not mint a token just to make the call.
   */
  visitorToken: string;
}

export interface EndUserStatus {
  found: boolean;
  isNewUser: boolean;
  hasPreferences: boolean;
  preferences?: Record<string, any>;
}

export interface ScreenshotOptions {
  redact?: boolean;
  scale?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
}

export interface PageStructure {
  title: string;
  url: string;
  elements: ElementInfo[];
  viewport: { width: number; height: number };
}

export interface ElementInfo {
  selector: string;
  type: string;
  text?: string;
  visible: boolean;
  position: DOMRect;
  attributes: Record<string, string>;
}

export interface ContextData {
  screenshot?: string;
  url: string;
  structure: PageStructure;
  timestamp: number;
  userProfile?: any;
}

export interface UIAction {
  type: 'click' | 'type' | 'select' | 'hover' | 'scroll';
  selector: string;
  value?: string;
  description?: string;
}

/**
 * Hybrid Target - Multi-strategy element targeting
 * Allows LLM to specify multiple ways to find an element
 */
export interface HybridTarget {
  selector?: string;      // CSS selector
  text?: string;          // Text content to search for
  tag?: string;           // HTML tag name
  ariaLabel?: string;     // ARIA label
  placeholder?: string;   // Placeholder text (for inputs)
  role?: string;          // ARIA role
  // Rich targeting fields for disambiguation (from Chrome recorder)
  labelText?: string;     // Associated form label (e.g., "Label" for a dropdown)
  optionText?: string;    // Dropdown option text (for clicking specific options)
  dropdownTriggerSelector?: string; // CSS selector to OPEN the dropdown (used with optionText)
  name?: string;          // HTML name attribute
  removeChipText?: string; // Multi-select: text of chip to remove (click X button)
  // Multi-select support
  selectedValues?: string[]; // Values to select for multi_select action
  // Fallback selectors (recorded during session capture)
  selectorFallbacks?: string[];  // Alternative selectors to try if primary fails
  strategy?: ('selector' | 'text' | 'tag' | 'ariaLabel' | 'placeholder' | 'role' | 'boundingBox' | 'labelText' | 'optionText' | 'removeChip')[];
  // DOM Introspection Layer (DIL) fields - server-resolved
  boundingBox?: { x: number; y: number; width: number; height: number };
  domIndex?: number;      // Index in DOM snapshot for direct lookup
  confidence?: number;    // Server's confidence in this target (0-1)
}

/**
 * Candidate element found during parallel strategy resolution
 */
export interface ResolutionCandidate {
  element: HTMLElement;
  strategies: string[];           // Which strategies found this element
  validationScore: number;        // 0-100 based on metadata match
  interactable: boolean;
  confidence: number;             // Final computed confidence
}

/**
 * Result of consensus-based target resolution
 */
export interface ResolutionResult {
  element: HTMLElement | null;
  confidence: number;
  strategies: string[];
  candidateCount: number;
  ambiguous: boolean;
  duration: number;
}

/**
 * DOM Element info for DOM Introspection Layer
 * Sent to server for accurate element targeting
 */
export interface DOMElementInfo {
  index: number;          // Index for fast lookup
  tag: string;
  text: string;           // Truncated visible text
  id: string;
  className: string;
  ariaLabel: string | null;
  placeholder: string | null;
  role: string | null;
  href: string | null;
  type: string | null;    // For inputs
  name: string | null;    // For form elements
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  isVisible: boolean;
  isInteractive: boolean;
  isInteractable: boolean;  // Whether element can actually receive interaction (not disabled, not obscured)
  parentTag: string | null;
  depth: number;          // DOM depth for hierarchy info
}

/**
 * DOM Snapshot sent to server
 */
export interface DOMSnapshot {
  timestamp: number;
  url: string;
  viewport: { width: number; height: number };
  elements: DOMElementInfo[];
  totalElements: number;
  interactiveCount: number;
  // Flag indicating if DOM changed since last snapshot - used to trigger vision analysis
  domChanged?: boolean;
  // Reason for the capture (periodic, mutation, immediate, etc.)
  captureReason?: string;
  // Correlation ID for tracking snapshots across client/server
  correlationId?: string;
}

/**
 * Action object from LLM - Hybrid UI Action
 */
export interface HybridUIAction {
  supportContract?: import('./support-step').SupportStep;
  planRunId?: string;
  /** Opaque server correlation id; completion must echo it without field values. */
  actionId?: string;
  action: 'click' | 'highlight' | 'type' | 'scroll' | 'hover' | 'focus' | 'navigate' | 'multi_select' | 'check_precondition' | 'verify' | 'file';
  target: HybridTarget;
  value?: string;         // For type actions
  description?: string;   // Human-readable description
  precondition?: {        // For check_precondition action
    type: 'option_exists' | 'element_visible' | 'element_enabled';
    optionText?: string;  // For option_exists: the option text to look for
    selector?: string;    // For element_visible/enabled: the element selector
  };
}

// Simple event emitter for SDK events
export class EventEmitter {
  private events: { [key: string]: Array<(...args: any[]) => void> } = {};

  on(event: string, listener: (...args: any[]) => void) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
    return this;
  }

  off(event: string, listener: (...args: any[]) => void) {
    if (!this.events[event]) return this;
    const idx = this.events[event].indexOf(listener);
    if (idx > -1) {
      this.events[event].splice(idx, 1);
    }
    return this;
  }

  emit(event: string, ...args: any[]) {
    if (!this.events[event]) return false;
    for (const listener of this.events[event]) {
      listener(...args);
    }
    return true;
  }
}

// ============================================================================
// FAST EXECUTION SYSTEM - Batch Types
// ============================================================================

/**
 * BatchStep - Represents a single step in a batched navigation sequence
 */
export interface BatchStep {
  action: 'click' | 'wait' | 'scroll';
  target?: HybridTarget;
  condition?: 'page_stable' | 'element_visible' | 'loading_complete';
  waitMs?: number;
}

/**
 * BatchedActionRequest - Request to execute multiple actions in sequence
 */
export interface BatchedActionRequest {
  steps: BatchStep[];
  introSpeech?: string;
  completionCallback?: () => void;
}
